import { useBus } from "@/core/store/bus";
import { useScopeQuery } from "@/core/store/scopeQuery";
import { useBasketStore } from "@/modules/shop/store/basket";
import { useBasketRulesStore } from "@/modules/shop/store/basket-rules";
import { useProductModificatorsComputedStore } from "@/modules/shop/store/product-modificators-computed";
import { RecalculateContext } from "@/modules/shop/store/types/orchestrator";
import { compareAttrs, OrderAttributesChanged, OrderAttributesChangedValues, } from "@/modules/shop/store/util/vorder";
import { useVorderApi } from "@/modules/shop/store/vorder-api";
import { useVorderCoupon } from "@/modules/shop/store/vorder-coupon";
import { useVorderPrize } from "@/modules/shop/store/vorder-prize";
import { useVorderState } from "@/modules/shop/store/vorder-state";
import { useVorderTotalStore } from "@/modules/shop/store/vorder-total";
import { OrderRestrictionContext } from "@/modules/shop/util/checkOrderRestriction";
import { useUserStore } from "@/modules/user/store/user";
import cloneDeep from "lodash-es/cloneDeep";
import { defineStore, storeToRefs } from "pinia";
import { computed, nextTick, watch } from "vue";
import {ssrSafeDebounce} from "@/core/util/ssrSafeDebounce";

const STORE_NAME = "vorder";

export const useVorderStore = defineStore(STORE_NAME, () => {

    const {bus} = useBus()
    const {registerScopeQuery} = useScopeQuery();

    const basketStore = useBasketStore();

    const modificatorsComputedStore = useProductModificatorsComputedStore();
    const basketRulesStore = useBasketRulesStore();
    const vorderState = useVorderState();
    const totalStore = useVorderTotalStore()
    const vorderPrize = useVorderPrize()
    const vorderCoupon = useVorderCoupon()
    const userStore = useUserStore()
    const vorderApi = useVorderApi()

    const {userPhone} = storeToRefs(userStore)
    const {prize} = storeToRefs(vorderPrize)

    const {updateVorderFromServer} = vorderApi

    const {
        vorderId,
        attrs,
        attrsPrev,
        attrsInfo,
        paymentTypes,
        transportTypes,
        vorderSynced,
        deliveries,
        coupon,
        orderRestrictions,
        orderOffice,
        deliveryPriceRules,
    } = storeToRefs(vorderState);

    const {basketItemsWatchable} = storeToRefs(basketStore);

    // METHODS

    const getContext = () => {
        // Получаем готовые значения из stores вместо вычисления
        const deliveryPriceTotal = totalStore.deliveryPrice
        const deliveryPriceSource = 0

        const basketPriceSource = basketStore.basketPrice
        const basketPriceTotal = totalStore.basketPriceTotal

        const orderPriceSource = basketPriceSource + deliveryPriceSource
        const orderPriceTotal = totalStore.orderPrice

        // Создаем OrderRestrictionContext для избежания циклических зависимостей
        const restrictionContext: OrderRestrictionContext = {
            attrs: attrs.value,
            office: orderOffice.value,
            basketStat: basketStore.basketItemsStat,
            basketItems: basketStore.basketItemsModels,
            orderPriceSource,
            orderPriceTotal,
        }

        if (vorderPrize.prize) {
            restrictionContext.prizeId = vorderPrize.prize.ID
        }

        if (vorderCoupon.coupon) {
            restrictionContext.couponId = vorderCoupon.coupon.ID
        }

        // Передаем контекст в функции пересчета
        const context: RecalculateContext = {
            deliveryPriceTotal,
            deliveryPriceSource,
            basketPriceSource,
            basketPriceTotal,
            orderPriceSource,
            orderPriceTotal,
            basketItemsCount: basketStore.basketItemsModels.length,
            restrictionContext
        }

        return context
    }

    function onAttrsChanged(
        attrsChanged: OrderAttributesChanged,
        attrsChangedValues: OrderAttributesChangedValues,
    ) {
        bus.emit('vorder:attrs.change', attrsChanged, attrsChangedValues);
    }

    // ✅ SSR-safe debounce: на сервере вызывается сразу, на клиенте с задержкой
    const onAttrsChangedDebounced = ssrSafeDebounce(onAttrsChanged, 50)

    const onAttrsChangedWrap = () => {
        const compare = compareAttrs(attrsPrev.value, attrs.value)
        attrsPrev.value = cloneDeep(attrs.value)
        if (compare.isChanged) {
            onAttrsChangedDebounced(compare.changed, compare.changedValues);
        }
    }

    const onAttrsChangedWrapDebounced = ssrSafeDebounce(onAttrsChangedWrap, 50)

    const recalcOrderState = async () => {
        const context = getContext()

        // Пересчитываем активные модификаторы
        modificatorsComputedStore.recalculateActiveModificators(context)

        // Пересчитываем активные правила корзины
        basketRulesStore.recalculateActiveBasketRules(context)

        await nextTick()

        basketRulesStore.ensureBenefitType()
    }

    // ✅ SSR-safe debounce: на сервере вызывается сразу, на клиенте с задержкой
    const recalcOrderStateDebounced = ssrSafeDebounce(recalcOrderState, 70)

    const attrsOrderableFields = computed(() => {
        return {
            BENEFIT_TYPE: attrs.value.BENEFIT_TYPE,
            TRANSPORT_TYPE: attrs.value.TRANSPORT_TYPE,
            PAYMENT_TYPE: attrs.value.PAYMENT_TYPE,
            DELIVERY_DEPARTMENT: attrs.value.DELIVERY_DEPARTMENT,
            PICKUP_DEPARTMENT: attrs.value.PICKUP_DEPARTMENT,
        }
    })

    // WATCHES
    watch(attrs, () => onAttrsChangedWrapDebounced(), {deep: true});

    // ORCHESTRATOR: Пересчет скидок и модификаторов при изменении attrs или корзины
    // Используем debounced версию для оптимизации производительности
    // Передаем контекст с данными для избежания циклических зависимостей
    watch(
        [
            attrsOrderableFields,
            basketItemsWatchable,
            prize
        ],
        () => {
            if (!process.env.SERVER) {
                recalcOrderStateDebounced()
            }
        },
        {
            deep: true,
            immediate: true,
        }
    )

    watch(userPhone, () => {
        if (userPhone.value) {
            attrs.value.PHONE = userPhone.value
        }
    }, {immediate: true})

    // SCOPE QUERY

    registerScopeQuery(
        STORE_NAME,
        "user",
        {
            vorder_current: {
                __fragment: "VorderCurrentFields",
            },
            order_restriction_list: {
                __fragment: "OrderRestrictionFields",
            },
        },
        (data) => {
            if (data.order_restriction_list)
                orderRestrictions.value = data.order_restriction_list;

            if (data.vorder_current)
                updateVorderFromServer(data.vorder_current)
        }
    );

    return {};
});
